import { BLUETOOTH_SPP_SERVICE_CLASS_ID } from '../bluetoothSerialSpp';
import type { SerialPortInfoExtended, SerialPortRequestOptionsExtended } from '../serialPortTypes';
import type { PrinterConnectionState } from '../types';
import type { PrinterTransport } from './PrinterTransport';

declare global {
  interface Navigator {
    serial?: Serial;
  }
}

export type WebSerialConnectionKind = 'usb' | 'bluetooth-spp';

const LS_LAST_USB_PORT = 'zeettu.printer.lastUsbSerialPort';

type RememberedUsbPort = { usbVendorId?: number; usbProductId?: number };

function readRememberedUsbPort(): RememberedUsbPort | null {
  try {
    const raw = globalThis.localStorage?.getItem(LS_LAST_USB_PORT);
    return raw ? (JSON.parse(raw) as RememberedUsbPort) : null;
  } catch {
    return null;
  }
}

function portInfo(port: SerialPort): SerialPortInfoExtended | undefined {
  return (port as SerialPort & { getInfo?: () => SerialPortInfoExtended }).getInfo?.();
}

/** True after a USB serial printer was connected once in this browser (its port grant persists). */
export function hasRememberedUsbSerialPort(): boolean {
  return readRememberedUsbPort() !== null;
}

export function forgetUsbSerialPort(): void {
  try {
    globalThis.localStorage?.removeItem(LS_LAST_USB_PORT);
  } catch {
    /* ignore */
  }
}

export type WebSerialTransportOptions = {
  baudRate?: number;
  /** USB cable (default) or paired Bluetooth Classic RFCOMM / SPP. */
  connectionKind?: WebSerialConnectionKind;
  /**
   * XP-P203A over RFCOMM: one `write()` with the full job, then drain the stream.
   * Avoids chunked USB-style pacing on Bluetooth serial.
   */
  singleShotWrite?: boolean;
  /** Written after open to verify the channel (e.g. TSPL `CLS` for LABEL mode). */
  connectProbeBytes?: Uint8Array;
  /** Log `[XP-P203A]` transport / payload lines (XP-P203A only). */
  xpP203aLogging?: boolean;
  onStateChange?: () => void;
};

export class WebSerialTransport implements PrinterTransport {
  readonly kind = 'serial' as const;

  private state: PrinterConnectionState = 'disconnected';

  private port: SerialPort | null = null;

  private writer: WritableStreamDefaultWriter<Uint8Array> | null = null;

  private readonly baudRate: number;

  private readonly connectionKind: WebSerialConnectionKind;

  private readonly singleShotWrite: boolean;

  private readonly isXpP203aSingleShotSpp: boolean;

  private readonly connectProbeBytes: Uint8Array | null;

  private readonly xpP203aLogging: boolean;

  constructor(private readonly opts: WebSerialTransportOptions = {}) {
    this.connectionKind = opts.connectionKind ?? 'usb';
    this.singleShotWrite = opts.singleShotWrite === true;
    if (opts.baudRate !== undefined && Number.isFinite(opts.baudRate) && opts.baudRate > 0) {
      this.baudRate = Math.floor(opts.baudRate);
    } else {
      const raw = import.meta.env?.VITE_PRINTER_SERIAL_BAUD ?? '9600';
      const parsed = Number.parseInt(raw, 10);
      this.baudRate = Number.isFinite(parsed) && parsed > 0 ? parsed : 9600;
    }
    this.isXpP203aSingleShotSpp = this.connectionKind === 'bluetooth-spp' && this.singleShotWrite;
    this.connectProbeBytes = opts.connectProbeBytes ?? null;
    this.xpP203aLogging = opts.xpP203aLogging === true;
  }

  isBluetoothSpp(): boolean {
    return this.connectionKind === 'bluetooth-spp';
  }

  getState(): PrinterConnectionState {
    return this.state;
  }

  private setState(next: PrinterConnectionState): void {
    this.state = next;
    this.opts.onStateChange?.();
  }

  async connect(): Promise<void> {
    if (typeof navigator === 'undefined' || !navigator.serial) {
      throw new Error(
        'Web Serial API is not available. Use Chrome/Edge on desktop with HTTPS, or install a compatible browser.',
      );
    }

    this.setState('connecting');
    try {
      const port = await this.requestPort();
      await this.openPort(port);
      this.rememberUsbPort(port);
      if (this.xpP203aLogging) {
        const transportLabel =
          this.connectionKind === 'bluetooth-spp' ? 'bluetooth-serial' : 'usb-serial';
        console.log(`[XP-P203A] Transport: ${transportLabel}`);
        if (this.connectionKind === 'bluetooth-spp') {
          console.log(`[XP-P203A] SPP UUID: ${BLUETOOTH_SPP_SERVICE_CLASS_ID.toUpperCase()}`);
        }
        console.log(`[XP-P203A] Baud: ${this.baudRate}`);
      }
      await this.validateConnectionProbe(port);
      this.setState('connected');
    } catch (e) {
      this.setState('error');
      await this.cleanup();
      throw e instanceof Error ? e : new Error(String(e));
    }
  }

  /**
   * Re-opens the previously granted USB printer port without a user gesture (page reload, cable re-plug).
   * Resolves false when no matching granted port is present.
   */
  async reconnectGranted(): Promise<boolean> {
    const remembered = readRememberedUsbPort();
    if (this.connectionKind !== 'usb' || !remembered || !navigator.serial?.getPorts) return false;
    const ports = await navigator.serial.getPorts();
    const port = ports.find((p) => {
      const info = portInfo(p);
      return info?.usbVendorId === remembered.usbVendorId && info?.usbProductId === remembered.usbProductId;
    });
    if (!port) return false;
    this.setState('connecting');
    try {
      await this.openPort(port);
      this.setState('connected');
      return true;
    } catch {
      await this.cleanup();
      this.setState('disconnected');
      return false;
    }
  }

  private rememberUsbPort(port: SerialPort): void {
    if (this.connectionKind !== 'usb') return;
    const { usbVendorId, usbProductId } = portInfo(port) ?? {};
    if (usbVendorId === undefined) return;
    try {
      globalThis.localStorage?.setItem(LS_LAST_USB_PORT, JSON.stringify({ usbVendorId, usbProductId }));
    } catch {
      /* quota */
    }
  }

  private async requestPort(): Promise<SerialPort> {
    if (this.connectionKind === 'bluetooth-spp') {
      const requestOptions: SerialPortRequestOptionsExtended = {
        allowedBluetoothServiceClassIds: [BLUETOOTH_SPP_SERVICE_CLASS_ID],
      };
      const port = await navigator.serial!.requestPort(requestOptions);
      this.assertSppServiceClass(port);
      return port;
    }
    const usbVendorId = this.parseOptionalHex(import.meta.env?.VITE_PRINTER_USB_VENDOR_ID);
    const usbProductId = this.parseOptionalHex(import.meta.env?.VITE_PRINTER_USB_PRODUCT_ID);
    const filters =
      usbVendorId != null && usbProductId != null
        ? [{ usbVendorId, usbProductId }]
        : undefined;
    return navigator.serial!.requestPort({ filters });
  }

  private async openPort(port: SerialPort): Promise<void> {
    await port.open({
      baudRate: this.baudRate,
      dataBits: 8,
      stopBits: 1,
      parity: 'none',
      bufferSize: 256,
    });
    this.port = port;
    if (!port.writable) {
      throw new Error('Serial port is not writable');
    }
    this.writer = port.writable.getWriter();
  }

  private assertSppServiceClass(port: SerialPort): void {
    if (this.connectionKind !== 'bluetooth-spp') return;
    const info = (port as SerialPort & { getInfo?: () => SerialPortInfoExtended }).getInfo?.();
    const serviceClassId = String(info?.bluetoothServiceClassId ?? '').trim().toLowerCase();
    if (!serviceClassId) return;
    if (serviceClassId !== BLUETOOTH_SPP_SERVICE_CLASS_ID) {
      throw new Error('Unsupported Bluetooth profile for XP-P203A. Expected RFCOMM SPP (00001101).');
    }
  }

  private async validateConnectionProbe(port: SerialPort): Promise<void> {
    if (!this.writer) return;
    const probe =
      this.connectProbeBytes ??
      (this.isXpP203aSingleShotSpp ? new Uint8Array([0x1b, 0x40]) : null);
    if (!probe) return;

    for (let attempt = 1; attempt <= 2; attempt += 1) {
      try {
        if (this.xpP203aLogging) {
          const preview = new TextDecoder('utf-8', { fatal: false }).decode(probe);
          console.log(`[XP-P203A] Connect probe: ${preview.replace(/\r\n/g, '\\n').trim()}`);
        }
        await this.writer.write(probe);
        await this.flushSerialWriter();
        return;
      } catch (e) {
        if (attempt >= 2) throw e instanceof Error ? e : new Error(String(e));
        await this.reopenCurrentPort(port);
      }
    }
  }

  private async reopenCurrentPort(port: SerialPort): Promise<void> {
    await this.cleanup();
    await this.openPort(port);
    this.assertSppServiceClass(port);
  }

  private parseOptionalHex(raw: string | undefined): number | null {
    if (!raw?.trim()) return null;
    const n = Number.parseInt(raw.trim(), 16);
    return Number.isFinite(n) ? n : null;
  }

  async disconnect(): Promise<void> {
    await this.cleanup();
    this.setState('disconnected');
  }

  private async cleanup(): Promise<void> {
    try {
      if (this.writer) {
        await this.writer.close().catch(() => {});
        this.writer = null;
      }
      if (this.port) {
        await this.port.close().catch(() => {});
        this.port = null;
      }
    } catch {
      this.port = null;
      this.writer = null;
    }
  }

  /** Release and re-acquire the stream writer so bytes drain to the RFCOMM channel. */
  private async flushSerialWriter(): Promise<void> {
    const port = this.port;
    if (!this.writer || !port?.writable) return;
    await this.writer.ready;
    await this.writer.close();
    this.writer = port.writable.getWriter();
    if (this.xpP203aLogging) {
      console.log('[XP-P203A] Flush Complete');
    }
  }

  private async tryReconnectAndRewrite(bytes: Uint8Array): Promise<boolean> {
    if (!this.isXpP203aSingleShotSpp || !this.port) return false;
    const port = this.port;
    for (let attempt = 1; attempt <= 2; attempt += 1) {
      try {
        await this.reopenCurrentPort(port);
        await this.validateConnectionProbe(port);
        await this.writer!.write(bytes);
        await this.flushSerialWriter();
        console.log('[XP-P203A] Print completed');
        return true;
      } catch {
        /* continue */
      }
    }
    return false;
  }

  async writeAuxiliary(bytes: Uint8Array): Promise<void> {
    await this.writePayload(bytes, { auxiliary: true });
  }

  async write(bytes: Uint8Array): Promise<void> {
    await this.writePayload(bytes, { auxiliary: false });
  }

  private async writePayload(bytes: Uint8Array, opts: { auxiliary: boolean }): Promise<void> {
    if (!this.writer) {
      throw new Error('Serial printer not connected');
    }
    if (!opts.auxiliary) {
      this.setState('printing');
    }
    const chunkSize = 256;
    try {
      if (this.singleShotWrite) {
        if (this.xpP203aLogging && !opts.auxiliary) {
          const preview = new TextDecoder('utf-8', { fatal: false }).decode(bytes);
          console.log(`[XP-P203A] Raw command preview: ${preview.replace(/\r\n/g, '\\n').slice(0, 300)}`);
          console.log(`[XP-P203A] Bytes written: ${bytes.length}`);
        }
        await this.writer.write(bytes);
        if (!opts.auxiliary) {
          await this.flushSerialWriter();
        }
        if (this.xpP203aLogging && !opts.auxiliary) {
          console.log('[XP-P203A] Print completed');
        }
      } else {
        if (this.xpP203aLogging && !opts.auxiliary) {
          const preview = new TextDecoder('utf-8', { fatal: false }).decode(bytes);
          console.log(`[XP-P203A] Raw command preview: ${preview.replace(/\r\n/g, '\\n').slice(0, 300)}`);
          console.log(`[XP-P203A] Bytes written: ${bytes.length}`);
        }
        for (let i = 0; i < bytes.length; i += chunkSize) {
          const chunk = bytes.subarray(i, i + chunkSize);
          await this.writer.write(chunk);
        }
        if (this.xpP203aLogging && !opts.auxiliary) {
          console.log('[XP-P203A] Print completed');
        }
      }
      if (!opts.auxiliary) {
        this.setState('connected');
      }
    } catch (e) {
      const stillLinked = Boolean(this.writer && this.port);
      const recovered = !opts.auxiliary && (await this.tryReconnectAndRewrite(bytes));
      if (recovered) {
        this.setState('connected');
        return;
      }
      if (stillLinked) {
        if (!opts.auxiliary) {
          this.setState('connected');
        }
      } else {
        await this.cleanup();
        this.setState('disconnected');
      }
      throw e instanceof Error ? e : new Error(String(e));
    }
  }
}
